import { isEqual } from "underscore";
import {
    types,
    flow,
    getParent,
    applySnapshot,
    getSnapshot
} from "mobx-state-tree";
import type { IAnyModelType } from "mobx-state-tree";

import { filterUndefinedProps } from "util/mst-utils";

const BaseBackboneModelMigrationStore = <T extends IAnyModelType>(store) => {
    const idAttribute = store.identifierAttribute;

    const model = {};

    const volatile = () => ({
        __model: null
    });

    const views = (self) => ({
        getAsObject() {
            return getSnapshot(self);
        },
        getAsJSON() {
            return JSON.stringify(self);
        }
    });

    const actions = (self) => ({
        /**
         * @summary Calls a method on the parent node to destroy this child
         */
        destroy() {
            // Parent cannot be inferred correctly due to complex mobx composition
            // I opted to have `any` here in the base template as opposed to the
            // implementation.
            // So we can have inference for the actual implementations
            (getParent(self, 2) as any).destroyChild(self);
        },

        /**
         * @summary Resets the model with new data
         */
        reset(data) {
            if (data[idAttribute] === self[idAttribute]) {
                applySnapshot(self, data);
            }
        }
    });

    const asyncActions = (self) => ({
        /**
         * @summary A generic base implimentation which returns a snapshot of the model.
         * This can be overriden by defining a bespoke fetch action in the combined store
         * @return {StreamObject}
         */
        fetch: flow(function* fetch() {
            yield new Promise(() => {
                return getSnapshot(self);
            });
        })
    });

    const backboneUtilityActions = (self) => ({
        __syncRead: flow(function* __syncRead() {
            try {
                const response = yield self.fetch();
                return response;
            } catch (error) {
                console.error(error);
                return error;
            }
        }),

        /**
         * @summary Update model with new data (if changed)
         * @param {StreamObject} data
         */
        __update(data) {
            if (data[idAttribute]) {
                self.__hasChanged(data) && applySnapshot(self, data);
            }
        },

        /**
         * @summary Reset model with new data
         * @param {StreamObject} data
         */
        __reset(data) {
            self.reset(data);
        },

        /**
         * @summary Compare parsed data with current model data and return boolean result
         * @param {StreamObject} data
         * @return {Boolean}
         */
        __hasChanged(data, filterUndefined = false) {
            return filterUndefined
                ? !isEqual(filterUndefinedProps(self), data)
                : !isEqual(self, data);
        },

        __setBackboneModel(model) {
            self.__model = model;
        }
    });

    const BaseStore = types
        .model(store.name, model)
        .volatile(volatile)
        .views(views)
        .actions(actions)
        .actions(asyncActions)
        .actions(backboneUtilityActions);

    return types.compose(BaseStore, store).named(store.name) as T;
};

export default BaseBackboneModelMigrationStore;
